/**
 * @file usePickingTaskMenu.tsx
 * @description Пункты меню «⋯» задания на сборку и их подтверждения
 * (phase19.14, фикс-батч W13; сведено к одному меню в phase19.23; переведено
 * на общий `RowActionsMenu` в phase19.24 этап 1, шаг 1).
 *
 * Хук, а не компонент: по конвенции шапки меню рисует
 * `DocumentHeaderActions` — ему нужен массив пунктов, а не готовая кнопка.
 * Своё меню задания (`PickingTaskMoreMenu`) было четвёртым «⋯» в проекте с
 * собственными размерами; теперь подача одна на все документы.
 *
 * Каждый пункт меняет чужую работу, поэтому идёт через подтверждение
 * (`PickingConfirmDialog`). Печать — исключение: она безопасна.
 *
 * При возврате в очередь сервер сохраняет собранные количества и снимает
 * отметки недостачи (`picking-tasks.service.ts#release`) — об этом прямо
 * сказано в подтверждении, иначе менеджер боится жать кнопку.
 */

import { Printer, RefreshCw, RotateCcw, UserMinus, XCircle } from "lucide-react";
import { useState, type ReactNode } from "react";

import { PickingConfirmDialog } from "./PickingConfirmDialog";
import type { PickingConfirmKind } from "./picking-confirm-text";
import type { RowAction } from "../../ui/RowActionsMenu";
import {
  canRebuildTask,
  isActivePickingTask,
  type PickingTaskDetailDto,
} from "../../../lib/api-picking";

interface Options {
  task: PickingTaskDetailDto;
  currentUserId: string | null;
  /** Право SHOP_WMS_MANAGE — без него в меню остаётся только печать. */
  canManage: boolean;
  busy: boolean;
  onRelease: () => void;
  onCancel: () => void;
  /** Пересобрать строки по текущим резервам заказа. */
  onRebuild: () => void;
  /** Печать листа комплектации; undefined — пункта нет. */
  onPrint?: () => void;
  printing?: boolean;
}

interface PickingTaskMenu {
  actions: RowAction[];
  /** Диалог подтверждения — страница кладёт его рядом с меню. */
  dialog: ReactNode;
}

export function usePickingTaskMenu({
  task,
  currentUserId,
  canManage,
  busy,
  onRelease,
  onCancel,
  onRebuild,
  onPrint,
  printing,
}: Options): PickingTaskMenu {
  const [confirm, setConfirm] = useState<PickingConfirmKind | null>(null);
  const mine = task.assignee?.id === currentUserId;
  const active = isActivePickingTask(task.status);
  const closed = task.status === "SHORTAGE" || task.status === "CANCELLED";
  // Вернуть в очередь можно и своё задание: без этого кладовщик, взявший
  // задание в работу, не может ни пересобрать его, ни отдать другому
  // (замечание пользователя 2026-09-16). Сервер разрешает release для
  // NEW/IN_PROGRESS/SHORTAGE/CANCELLED.
  const releasable = canManage && ((active && !!task.assignee) || closed);

  function run() {
    const kind = confirm;
    setConfirm(null);
    if (kind === "cancel") onCancel();
    else if (kind === "rebuild") onRebuild();
    else if (kind) onRelease();
  }

  const actions: RowAction[] = [];
  if (onPrint) {
    actions.push({
      key: "print",
      label: printing ? "Готовим лист комплектации…" : "Лист комплектации",
      icon: Printer,
      run: onPrint,
    });
  }
  if (releasable) {
    actions.push({
      key: "release",
      label: mine && active ? "Вернуть в очередь" : "Освободить задание",
      icon: mine && active ? RotateCcw : UserMinus,
      run: () => setConfirm(mine && active ? "return" : "release"),
    });
  }
  if (canManage && canRebuildTask(task)) {
    actions.push({
      key: "rebuild",
      label: "Пересобрать задание по текущим резервам",
      icon: RefreshCw,
      run: () => setConfirm("rebuild"),
    });
  }
  // Деструктивное действие — последним (правило A6).
  if (canManage && active) {
    actions.push({
      key: "cancel",
      label: "Отменить задание",
      icon: XCircle,
      danger: true,
      run: () => setConfirm("cancel"),
    });
  }

  return {
    actions,
    dialog: (
      <PickingConfirmDialog
        kind={confirm}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={run}
      />
    ),
  };
}
